// グリッド/リスト選択とフォルダドロップ移動
import { ref, computed } from 'vue';
import { filterVideoItems, isFolderItem } from './useItemTypeGuards';
import { sortFoldersByHierarchy } from './useFolderCreate';

export const useDragAndDrop = ({
  items,
  isInShareBasket,
  shareBasketVideoIds,
  setShareBasketItems,
  selectItem,
  clearSelection,
  reloadCurrentFolder,
  selectedFolderId,
  allowedFolderIds,
  removeFromShareBasket,
  folders = ref([]),
  shareBasketFolderIds = ref([]),
}) => {
  const moveEndpoint = ref('');
  const moveFailedMessage = ref('Failed to move files.');
  // フォルダも一緒に移す API (admin のみ)。canMoveFolders が false のときはファイルだけを今のまま移す
  const folderMoveEndpoint = ref('');
  const folderMoveIntoSelfMessage = ref('');
  const canMoveFolders = ref(false);
  const isDragging = ref(false);
  const dragTargetFolderId = ref(null);
  // TOP のドロップ先ハイライトは dragTargetFolderId (null="ドロップ先なし"の意味で使用中) を
  // 流用せず専用の ref で管理する。流用すると「どこもホバーしていない間ずっとTOPが光る」誤点灯になる。
  const isTopDropTarget = ref(false);
  const dragVideoIds = ref([]);
  const dragFolderIds = ref([]);
  let dragPreviewEl = null;

  const setMoveOptions = ({
    endpoint = '',
    failedMessage = null,
    folderEndpoint = '',
    intoSelfMessage = '',
    canMoveFolders: canMove = false,
  } = {}) => {
    moveEndpoint.value = endpoint || '';
    if (failedMessage) {
      moveFailedMessage.value = failedMessage;
    }
    folderMoveEndpoint.value = folderEndpoint || '';
    folderMoveIntoSelfMessage.value = intoSelfMessage || '';
    canMoveFolders.value = !!canMove;
  };

  // 掴んだフォルダ自身とその配下 (folders.value の parent_id でたどる)。落とし先にしない
  const noDropFolderIds = computed(() => {
    const result = new Set(dragFolderIds.value.map((id) => Number(id)));
    if (!result.size) {
      return [];
    }
    const list = Array.isArray(folders.value) ? folders.value : [];
    let added = true;
    while (added) {
      added = false;
      list.forEach((f) => {
        const id = Number(f.id);
        if (!result.has(id) && f.parent_id !== null && f.parent_id !== undefined
          && result.has(Number(f.parent_id))) {
          result.add(id);
          added = true;
        }
      });
    }
    return Array.from(result);
  });
  const isNoDropFolder = (folderId) => noDropFolderIds.value.includes(Number(folderId));

  const cleanupDragPreview = () => {
    if (dragPreviewEl && dragPreviewEl.parentNode) {
      dragPreviewEl.parentNode.removeChild(dragPreviewEl);
    }
    dragPreviewEl = null;
  };

  const getSelectedItems = () => {
    const idSet = new Set(dragVideoIds.value.map((id) => String(id)));
    const list = filterVideoItems(items.value);
    const videos = list.filter((item) => idSet.has(String(item?.id)));
    const folderIdSet = new Set(dragFolderIds.value.map((id) => String(id)));
    const folderItems = (Array.isArray(items.value) ? items.value : [])
      .filter((item) => isFolderItem(item) && folderIdSet.has(String(item?.id)));
    return [...videos, ...folderItems];
  };

  const createDragPreview = (selectedItems) => {
    if (!Array.isArray(selectedItems) || selectedItems.length < 1) {
      return null;
    }
    const first = selectedItems[0] || {};
    const preview = document.createElement('div');
    preview.className = 'drag-preview';
    preview.style.position = 'fixed';
    preview.style.top = '-1000px';
    preview.style.left = '-1000px';
    preview.style.pointerEvents = 'none';

    const thumb = document.createElement('div');
    thumb.className = 'drag-preview__thumb';
    if (first.thumbnail_url) {
      thumb.style.backgroundImage = `url("${first.thumbnail_url}")`;
    } else {
      thumb.classList.add('is-empty');
    }

    const count = document.createElement('div');
    count.className = 'drag-preview__count';
    count.textContent = String(selectedItems.length);

    preview.appendChild(thumb);
    preview.appendChild(count);
    document.body.appendChild(preview);
    return preview;
  };

  const isFolderAllowed = (folderId) => {
    if (allowedFolderIds.value === null) {
      return true;
    }
    const numericId = Number(folderId);
    return (allowedFolderIds.value || []).some((id) => Number(id) === numericId);
  };

  const resolveMovableIds = (targetFolderId, ids) => {
    const targetId = String(targetFolderId ?? '');
    const idSet = new Set((ids || []).map((id) => String(id)));
    const list = filterVideoItems(items.value);
    return list
      .filter((item) => idSet.has(String(item?.id)))
      .filter((item) => String(item?.folder_id ?? '') !== targetId)
      .map((item) => item.id);
  };

  const moveVideosToFolder = async (folderId, ids) => {
    if (!moveEndpoint.value) {
      return;
    }
    if (!ids.length) {
      return;
    }
    try {
      const payload = folderId === null
        ? { to_root: 1, video_ids: ids }
        : { folder_id: folderId, video_ids: ids };
      await window.axios.post(moveEndpoint.value, payload);
      clearSelection();
      ids.forEach((id) => removeFromShareBasket(id));
      await reloadCurrentFolder();
    } catch (error) {
      console.error('Move failed:', error);
      window.alert(moveFailedMessage.value || 'Failed to move files.');
    }
  };

  // 選んだフォルダのうち、すでに移す先の直下にあるものは除く (サーバーも何もしないが、送らない)
  const resolveMovableFolderIds = (targetFolderId, folderIds) => {
    const targetId = String(targetFolderId ?? 0);
    const list = Array.isArray(folders.value) ? folders.value : [];
    return folderIds.filter((id) => {
      const node = list.find((f) => String(f.id) === String(id));
      return String(node?.parent_id ?? 0) !== targetId;
    });
  };

  const applyMovedFolders = (movedFolders) => {
    if (!Array.isArray(movedFolders) || !movedFolders.length) {
      return;
    }
    const byId = new Map(movedFolders.map((f) => [String(f.id), f]));
    const next = (Array.isArray(folders.value) ? folders.value : []).map((f) => {
      const moved = byId.get(String(f.id));
      if (!moved) {
        return f;
      }
      const updated = {
        ...f,
        parent_id: moved.parent_id,
        depth: moved.depth,
        sort_order: moved.sort_order,
      };
      if (moved.desktop_upload !== undefined) {
        updated.desktop_upload = moved.desktop_upload;
      }
      return updated;
    });
    folders.value = sortFoldersByHierarchy(next);
  };

  const moveItemsToFolder = async (folderId, folderIds, videoIds) => {
    if (!folderMoveEndpoint.value) {
      return;
    }
    try {
      const payload = folderId === null ? { to_root: 1 } : { folder_id: folderId };
      if (folderIds.length) {
        payload.folder_ids = folderIds;
      }
      if (videoIds.length) {
        payload.video_ids = videoIds;
      }
      const response = await window.axios.post(folderMoveEndpoint.value, payload);
      applyMovedFolders(response?.data?.folders);
      clearSelection();
      videoIds.forEach((id) => removeFromShareBasket(id));
      folderIds.forEach((id) => removeFromShareBasket(id, 'folder'));
      await reloadCurrentFolder();
    } catch (error) {
      console.error('Move failed:', error);
      const serverMessage = error?.response?.status === 422 ? error.response.data?.message : null;
      window.alert(serverMessage || moveFailedMessage.value || 'Failed to move files.');
    }
  };

  // folderId が null のときは TOP。ファイルだけなら今の videos/move、フォルダが入っていれば folders/move
  const dropOnTarget = async (folderId) => {
    const selectedFolders = canMoveFolders.value ? dragFolderIds.value.slice() : [];
    const ids = dragVideoIds.value.length
      ? dragVideoIds.value
      : (selectedFolders.length ? [] : Array.from(shareBasketVideoIds.value));
    if (selectedFolders.length && folderId !== null && isNoDropFolder(folderId)) {
      // 選んだフォルダ自身の上で離したときは何も出さない。配下のときだけ知らせる
      if (!selectedFolders.some((id) => Number(id) === Number(folderId))) {
        window.alert(folderMoveIntoSelfMessage.value);
      }
      return;
    }
    const movableIds = resolveMovableIds(folderId, ids);
    if (!selectedFolders.length) {
      if (!movableIds.length) {
        return;
      }
      await moveVideosToFolder(folderId, movableIds);
      return;
    }
    const movableFolderIds = resolveMovableFolderIds(folderId, selectedFolders);
    if (!movableFolderIds.length && !movableIds.length) {
      return;
    }
    await moveItemsToFolder(folderId, movableFolderIds, movableIds);
  };

  const handleFolderDrop = async (folderId) => {
    if (!isDragging.value) {
      return;
    }
    dragTargetFolderId.value = null;
    isDragging.value = false;
    cleanupDragPreview();

    if (!isFolderAllowed(folderId)) {
      return;
    }
    await dropOnTarget(folderId);
    dragVideoIds.value = [];
    dragFolderIds.value = [];
  };

  const handleFolderDragEnter = (folderId, event) => {
    if (!isDragging.value) {
      return;
    }
    if (!isFolderAllowed(folderId)) {
      return;
    }
    if (isNoDropFolder(folderId)) {
      return;
    }
    dragTargetFolderId.value = folderId;
  };

  const handleFolderDragLeave = (folderId, event) => {
    if (event && event.relatedTarget && event.currentTarget
      && typeof event.currentTarget.contains === 'function'
      && event.currentTarget.contains(event.relatedTarget)) {
      return;
    }
    if (dragTargetFolderId.value === folderId) {
      dragTargetFolderId.value = null;
    }
  };

  const handleTopDrop = async () => {
    if (!isDragging.value) {
      return;
    }
    isTopDropTarget.value = false;
    isDragging.value = false;
    cleanupDragPreview();

    if (!isFolderAllowed(null)) {
      return;
    }
    await dropOnTarget(null);
    dragVideoIds.value = [];
    dragFolderIds.value = [];
  };

  const handleTopDragEnter = (event) => {
    if (!isDragging.value) {
      return;
    }
    if (!isFolderAllowed(null)) {
      return;
    }
    isTopDropTarget.value = true;
  };

  const handleTopDragLeave = (event) => {
    if (event && event.relatedTarget && event.currentTarget
      && typeof event.currentTarget.contains === 'function'
      && event.currentTarget.contains(event.relatedTarget)) {
      return;
    }
    isTopDropTarget.value = false;
  };

  const handleDragStart = (item, index, event) => {
    if (!item) {
      return;
    }
    const target = event?.target;
    if (target && target.closest && target.closest('input[type="checkbox"]')) {
      event.preventDefault();
      return;
    }
    const isFolder = isFolderItem(item);
    if (isFolder && !canMoveFolders.value) {
      event?.preventDefault?.();
      return;
    }
    if (!isInShareBasket(item.id, isFolder ? 'folder' : 'video')) {
      setShareBasketItems([item]);
      selectItem(item, index);
    }
    const list = filterVideoItems(items.value);
    const visibleIds = new Set(list.map((entry) => String(entry?.id)));
    const ids = Array.from(shareBasketVideoIds.value).filter((id) => visibleIds.has(String(id)));
    dragVideoIds.value = ids;
    // 今の一覧に見えている選択のフォルダだけを集める (admin のとき)
    const visibleFolderIds = new Set(
      (Array.isArray(items.value) ? items.value : [])
        .filter((entry) => isFolderItem(entry))
        .map((entry) => String(entry?.id))
    );
    dragFolderIds.value = canMoveFolders.value
      ? Array.from(shareBasketFolderIds.value).filter((id) => visibleFolderIds.has(String(id)))
      : [];
    isDragging.value = true;
    if (event?.dataTransfer) {
      const selectedItems = getSelectedItems();
      cleanupDragPreview();
      if (selectedItems.length > 0) {
        dragPreviewEl = createDragPreview(selectedItems);
        if (dragPreviewEl) {
          const offsetX = dragPreviewEl.offsetWidth / 2;
          const offsetY = dragPreviewEl.offsetHeight - 8;
          event.dataTransfer.setDragImage(dragPreviewEl, offsetX, offsetY);
        }
      }
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/plain', (ids.length ? ids : dragFolderIds.value).join(','));
    }
  };

  const handleDragEnd = () => {
    isDragging.value = false;
    dragTargetFolderId.value = null;
    isTopDropTarget.value = false;
    dragVideoIds.value = [];
    dragFolderIds.value = [];
    cleanupDragPreview();
  };

  return {
    moveFailedMessage,
    isDragging,
    dragTargetFolderId,
    isTopDropTarget,
    noDropFolderIds,
    setMoveOptions,
    handleDragStart,
    handleDragEnd,
    handleFolderDrop,
    handleFolderDragEnter,
    handleFolderDragLeave,
    handleTopDrop,
    handleTopDragEnter,
    handleTopDragLeave,
  };
};
